import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  User, Star, BookOpen, Users, Clock, 
  CheckCircle, Award, Calendar, PlayCircle,
  Globe, Mail, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '/api';

export default function InstructorLanding() {
  const { username } = useParams();
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';
  
  const [instructor, setInstructor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchInstructorData();
  }, [username]);

  const fetchInstructorData = async () => {
    try {
      // Try to find instructor by referral code or user id
      const response = await axios.get(`${API}/api/instructor/public/${username}`);
      setInstructor(response.data.instructor);
      setCourses(response.data.courses || []);
    } catch (error) {
      console.error('Error fetching instructor:', error);
      setError(isEnglish ? 'Instructor not found' : 'Eğitmen bulunamadı');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (price) => {
    return new Intl.NumberFormat(isEnglish ? 'en-US' : 'tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
            <h2 className="text-xl font-bold mb-2">{error}</h2>
            <p className="text-gray-500 mb-4">
              {isEnglish 
                ? 'The instructor you are looking for does not exist.'
                : 'Aradığınız eğitmen mevcut değil.'}
            </p>
            <Link to="/instructors">
              <Button>{isEnglish ? 'Browse Instructors' : 'Eğitmenlere Göz At'}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 wellness-gradient">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-16">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-32 md:w-40 md:h-40 rounded-full bg-white/20 flex items-center justify-center overflow-hidden">
              {instructor?.profile_image ? (
                <img 
                  src={instructor.profile_image} 
                  alt={instructor.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="h-16 w-16 text-white/70" />
              )}
            </div>
            
            <div className="text-center md:text-left">
              <h1 className="text-3xl md:text-4xl font-bold mb-2">
                {instructor?.full_name}
              </h1>
              <p className="text-purple-100 text-lg mb-4">
                {instructor?.expertise_area || (isEnglish ? 'Pilates Instructor' : 'Pilates Eğitmeni')}
              </p>
              
              <div className="flex flex-wrap justify-center md:justify-start gap-4">
                <Badge className="bg-white/20 text-white px-3 py-1">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {isEnglish ? 'Verified Instructor' : 'Onaylı Eğitmen'}
                </Badge>
                <Badge className="bg-white/20 text-white px-3 py-1">
                  <BookOpen className="h-4 w-4 mr-1" />
                  {courses.length} {isEnglish ? 'Courses' : 'Kurs'}
                </Badge>
                {instructor?.total_students > 0 && (
                  <Badge className="bg-white/20 text-white px-3 py-1">
                    <Users className="h-4 w-4 mr-1" />
                    {instructor.total_students} {isEnglish ? 'Students' : 'Öğrenci'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Bio & Info */}
          <div className="lg:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>{isEnglish ? 'About' : 'Hakkında'}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  {instructor?.bio || (isEnglish 
                    ? 'Professional instructor dedicated to helping you achieve your wellness goals.'
                    : 'Sağlık hedeflerinize ulaşmanıza yardımcı olmaya adanmış profesyonel eğitmen.')}
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{isEnglish ? 'Expertise' : 'Uzmanlık Alanları'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-2">
                  {(instructor?.expertise_areas || ['Pilates', 'Yoga', 'Wellness']).map((area, idx) => (
                    <Badge key={idx} variant="outline">
                      {area}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>

            {instructor?.certifications && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    {isEnglish ? 'Certifications' : 'Sertifikalar'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {instructor.certifications.map((cert, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {cert}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Column - Courses */}
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold mb-6">
              {isEnglish ? 'Courses by ' : ''}{instructor?.full_name}{!isEnglish ? ' Kursları' : '\'s Courses'}
            </h2>
            
            {courses.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {courses.map((course) => (
                  <Card key={course.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                    <div className="aspect-video bg-gray-100 relative">
                      {course.thumbnail_url ? (
                        <img 
                          src={course.thumbnail_url} 
                          alt={course.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-100 to-pink-100">
                          <PlayCircle className="h-12 w-12 text-purple-300" />
                        </div>
                      )}
                      {course.lesson_type === 'live' && (
                        <Badge className="absolute top-2 right-2 bg-red-500">
                          <PlayCircle className="h-3 w-3 mr-1" />
                          {isEnglish ? 'Live' : 'Canlı'}
                        </Badge>
                      )}
                    </div>
                    
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-2 line-clamp-2">{course.title}</h3>
                      
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                        <span className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {course.duration || 60} dk
                        </span>
                        <Badge variant="outline">{course.level || 'beginner'}</Badge>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-purple-600">
                          {formatCurrency(course.price)}
                        </span>
                        <Link to={`/lessons/${course.id}`}>
                          <Button size="sm">
                            {isEnglish ? 'View Course' : 'Kursa Git'}
                          </Button>
                        </Link>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="p-8 text-center text-gray-500">
                  <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>{isEnglish ? 'No courses yet' : 'Henüz kurs yok'}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
